// src/pages/components/topBar/CoinBalance.jsx
//
// Wallet UI — real API + Stripe Elements for coin purchases.
//
import React, { Component, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Modal from '../Modal';
import FigCoinSvg from '../../../assets/logo.svg';
import { getAuthHeaders, urlMap } from '../../../utils';
import { getWebLanguage } from '../../../i18n/sharedTranslations';
import { NotificationContext } from '../../../state/notifications/NotificationContext';

const PACKAGE_META = [
  {
    key: 'spark',
    label: 'Spark',
    runs: '~1 manuscript analysis',
  },
  {
    key: 'standard',
    label: 'Standard',
    runs: '~3 manuscript analyses',
    featured: true,
  },
  {
    key: 'pro',
    label: 'Pro',
    runs: '~6 manuscript analyses',
  },
];

function toPackageList(pricingPayload) {
  const apiPackages = pricingPayload?.coinPackages || {};
  return PACKAGE_META.map((pkg) => {
    const fromApi = apiPackages[pkg.key] || {};
    return {
      ...pkg,
      coins: Number(fromApi.coins || 0),
      amountCents: Number(fromApi.amountCents || 0),
      currency: fromApi.currency || 'usd',
      price: fromApi.displayPrice || '$0.00',
    };
  });
}

// ─── Inner checkout form — uses Stripe hooks, so must be a functional component ─
function WalletCheckout({ price, onSuccess, onCancel }) {
  const stripe   = useStripe();
  const elements = useElements();
  const [paying, setPaying]  = useState(false);
  const [error,  setError]   = useState(null);
  const [digitalCreditsConsent, setDigitalCreditsConsent] = useState(false);

  const handlePay = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!digitalCreditsConsent) {
      setError('You must acknowledge the digital credits delivery terms to proceed.');
      return;
    }
    setPaying(true);
    setError(null);

    const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (stripeError) {
      setError(stripeError.message ?? 'Payment failed.');
      setPaying(false);
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    }
  };

  return (
    <form onSubmit={handlePay} className="wallet-stripe-form">
      <fieldset className="wallet-card-fieldset">
        <legend className="wallet-card-legend">Card details</legend>
        <PaymentElement />
      </fieldset>
      <p className="wallet-secure-badge">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="wallet-secure-badge__icon">
          <path fillRule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clipRule="evenodd" />
        </svg>
        Secure purchase with Stripe
      </p>
      <p className="wallet-legal-links">
        <a href="https://storyclub.ai/terms-of-service/" target="_blank" rel="noopener noreferrer">Terms</a>
        <span aria-hidden="true">•</span>
        <a href="https://storyclub.ai/refund-and-cancellation/" target="_blank" rel="noopener noreferrer">Refund &amp; Cancellation</a>
        <span aria-hidden="true">•</span>
        <a href="https://storyclub.ai/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy</a>
      </p>
      
      {/* Digital Credits Delivery Consent */}
      <div className="wallet-consent-group">
        <label className="wallet-consent-checkbox">
          <input
            type="checkbox"
            checked={digitalCreditsConsent}
            onChange={(e) => {
              setDigitalCreditsConsent(e.target.checked);
              if (e.target.checked) setError(null);
            }}
            disabled={paying}
          />
          <span className="wallet-consent-text">
            I explicitly request that delivery of digital credits starts immediately after payment. 
            I acknowledge that the right of withdrawal is forfeited when delivery has begun, 
            in accordance with the Withdrawal Rights Act § 22 letter n.
          </span>
        </label>
      </div>

      {error && <p className="wallet-error">{error}</p>}
      <div className="wallet-checkout-actions">
        <button
          type="button"
          className="wallet-cancel-btn"
          onClick={onCancel}
          disabled={paying}
        >
          Back
        </button>
        <button
          type="submit"
          className="wallet-pay-btn"
          disabled={paying || !stripe || !digitalCreditsConsent}
        >
          {paying ? 'Processing…' : `Pay ${price}`}
        </button>
      </div>
    </form>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────
function formatTxType(type) {
  switch (type) {
    case 'free_grant': return 'Free figs';
    case 'purchase':   return 'Purchase';
    case 'usage':      return 'AI call';
    case 'refund':     return 'Refund';
    default:           return type;
  }
}

function formatTxSource(tx) {
  if (tx?.type !== 'usage') return '';

  const source = String(tx?.metadata?.source || '').trim();
  if (!source) return 'Unknown source';

  switch (source) {
    case 'coach_chapter_level':
      return 'Coach chapter level';
    case 'reflection_chapter_level':
      return 'Reflection chapter level';
    case 'coach_project_level':
      return 'Coach project level';
    case 'agent_step': {
      const level = String(tx?.metadata?.level || '').trim();
      const stepName = String(tx?.metadata?.stepName || tx?.metadata?.step || '').trim();
      if (stepName && level) return `Agent step (${level}): ${stepName}`;
      if (stepName) return `Agent step: ${stepName}`;
      return level ? `Agent step (${level} level)` : 'Agent step';
    }
    default:
      return source.replace(/_/g, ' ');
  }
}

// ─── Main CoinBalance component ───────────────────────────────────────────────
class CoinBalance extends Component {
  static contextType = NotificationContext;

  constructor(props) {
    super(props);
    this.state = {
      balance: null,
      publishableKey: '',
      loading: true,
      modalOpen: false,
      activeTab: 'subscription', // 'buy' | 'subscription' | 'history'
      selected: 'standard',
      clientSecret: null,
      creatingIntent: false,
      intentError: null,
      paymentSuccess: false,
      transactions: [],
      txLoading: false,
      pricingLoading: false,
      pricingError: null,
      pricing: null,
      subscriptionStarting: false,
      subscriptionError: null,
      subscriptionStatus: null,
    };

    // Instance properties for refs
    this.pollRef = null;
    this.stripePromiseRef = null;
    this.stripeKeyRef = '';
    this.notificationUnregister = null;
    this.walletSubscriptionHandler = null;
  }

  componentDidMount() {
    this.fetchBalance();
    this.fetchPricing();
    this.fetchSubscriptionStatus();

    // Re-fetch balance when a coach message triggers a wallet debit
    this.notificationUnregister = this.context?.registerWalletRefresh?.(this.fetchBalance);

    this.walletSubscriptionHandler = () => {
      this.setState({
        clientSecret: null,
        intentError: null,
        paymentSuccess: false,
        subscriptionError: null,
        activeTab: 'subscription',
        modalOpen: true,
      });
    };
    window.addEventListener('storyclub:open-wallet-subscription', this.walletSubscriptionHandler);

    // Detect Stripe redirect-back (e.g. from Klarna)
    const params = new URLSearchParams(window.location.search);
    if (params.get('redirect_status') === 'succeeded') {
      this.fetchBalance();
      const url = new URL(window.location.href);
      ['payment_intent', 'payment_intent_client_secret', 'redirect_status'].forEach(
        (k) => url.searchParams.delete(k)
      );
      window.history.replaceState({}, '', url.toString());
    }
  }

  componentWillUnmount() {
    this.notificationUnregister?.();
    clearInterval(this.pollRef);
    if (this.walletSubscriptionHandler) {
      window.removeEventListener('storyclub:open-wallet-subscription', this.walletSubscriptionHandler);
    }
  }

  getStripe = (key) => {
    if (!key) return null;
    if (this.stripeKeyRef !== key) {
      this.stripeKeyRef = key;
      this.stripePromiseRef = loadStripe(key);
    }
    return this.stripePromiseRef;
  };

  fetchBalance = async () => {
    try {
      const res = await fetch(urlMap.walletBalance, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ balance: data.balance, publishableKey: data.publishableKey ?? '' });
    } catch {
      // silently fail — user may not be logged in
    } finally {
      this.setState({ loading: false });
    }
  };

  fetchPricing = async () => {
    this.setState({ pricingLoading: true, pricingError: null });
    try {
      const language = getWebLanguage();
      const res = await fetch(`${urlMap.walletPricing}?language=${encodeURIComponent(language)}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not load pricing');
      this.setState({
        pricing: data,
        publishableKey: data.publishableKey || this.state.publishableKey || '',
      });
    } catch (e) {
      this.setState({ pricingError: e?.message || 'Could not load pricing' });
    } finally {
      this.setState({ pricingLoading: false });
    }
  };

  fetchSubscriptionStatus = async () => {
    try {
      const res = await fetch(urlMap.walletSubscriptionStatus, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return;
      this.setState({ subscriptionStatus: data });
    } catch {
      // ignore
    }
  };

  fetchTransactions = async () => {
    this.setState({ txLoading: true });
    try {
      const res = await fetch(urlMap.walletTransactions, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      this.setState({ transactions: data.transactions ?? [] });
    } catch {
      // ignore
    } finally {
      this.setState({ txLoading: false });
    }
  };

  openModal = () => {
    this.setState({
      clientSecret: null,
      intentError: null,
      paymentSuccess: false,
      activeTab: 'subscription',
      modalOpen: true,
    });
    this.fetchPricing();
    this.fetchSubscriptionStatus();
  };

  closeModal = () => {
    clearInterval(this.pollRef);
    this.setState({ modalOpen: false });
  };

  openHistory = () => {
    this.setState({ activeTab: 'history', modalOpen: true });
    this.fetchTransactions();
  };

  handleBuy = async () => {
    this.setState({ creatingIntent: true, intentError: null });
    try {
      const language = getWebLanguage();
      const res = await fetch(urlMap.walletPurchaseIntent, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ packageKey: this.state.selected, language }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'FAILED');
      this.setState({ clientSecret: data.clientSecret });
    } catch (e) {
      this.setState({ intentError: e.message });
    } finally {
      this.setState({ creatingIntent: false });
    }
  };

  handleStartSubscriptionCheckout = async () => {
    this.setState({ subscriptionStarting: true, subscriptionError: null });
    try {
      const language = getWebLanguage();
      const res = await fetch(urlMap.walletSubscriptionCheckout, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ language }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not start checkout');
      if (!data?.url) throw new Error('Missing checkout URL');
      window.location.assign(data.url);
    } catch (e) {
      this.setState({ subscriptionError: e?.message || 'Could not start checkout' });
    } finally {
      this.setState({ subscriptionStarting: false });
    }
  };

  handlePaymentSuccess = async (paymentIntentId) => {
    this.setState({ clientSecret: null, paymentSuccess: true });

    if (paymentIntentId) {
      try {
        await fetch(urlMap.walletPurchaseConfirm, {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId }),
        });
      } catch {
        // ignore and rely on webhook + polling fallback
      }
    }

    // Poll every 1.5s for up to 15s waiting for webhook to credit coins
    let attempts = 0;
    const prevBalance = this.state.balance;
    this.pollRef = setInterval(async () => {
      attempts++;
      try {
        const res = await fetch(urlMap.walletBalance, { headers: getAuthHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (data.balance !== prevBalance) {
          this.setState({ balance: data.balance });
          clearInterval(this.pollRef);
          this.fetchTransactions();
          setTimeout(() => {
            this.setState({ paymentSuccess: false, activeTab: 'history' });
          }, 1500);
        }
      } catch { /* ignore */ }
      if (attempts >= 20) clearInterval(this.pollRef);
    }, 1500);
  };

  render() {
    const {
      balance,
      publishableKey,
      loading,
      modalOpen,
      activeTab,
      selected,
      clientSecret,
      creatingIntent,
      intentError,
      paymentSuccess,
      transactions,
      txLoading,
      pricing,
      pricingLoading,
      pricingError,
      subscriptionStarting,
      subscriptionError,
      subscriptionStatus,
    } = this.state;

    if (loading) return null;

    const isEmpty = Number(balance) <= 0;
    const packages = toPackageList(pricing);
    const selectedPkg = packages.find((p) => p.key === selected);
    const proPricing = pricing?.subscription;
    const proActive = !!subscriptionStatus?.isPro;
    const walletLabel = proActive ? 'Pro Active' : 'Unlock Pro';
    const shouldInitStripe = modalOpen && activeTab === 'buy' && !!clientSecret && !!publishableKey;
    const stripePromise = shouldInitStripe ? this.getStripe(publishableKey) : null;

    return (
      <>
        {/* ── Topbar balance button ───────────────────────────────────── */}
        <button
          className={`icon-btn coin-balance-btn${isEmpty ? ' is-empty' : ''}`}
          onClick={this.openModal}
          aria-label={`${walletLabel}. ${balance} fig coins. Click to open wallet.`}
          type="button"
        >
          <img src={FigCoinSvg} className="fig-coin-icon" alt="" aria-hidden="true" />
          <span className="coin-balance-copy">
            <span className="coin-balance-title">{walletLabel}</span>
            <span className="coin-balance-count">{Number(balance) % 1 === 0 ? balance : Number(balance).toFixed(2)} fig coins</span>
          </span>
        </button>

        {/* ── Purchase modal ──────────────────────────────────────────── */}
        <Modal isOpen={modalOpen} onClose={this.closeModal}>
          <div className="wallet-modal">

            <h2 className="wallet-modal__title">
              <img src={FigCoinSvg} className="fig-coin-icon fig-coin-icon--lg" alt="" aria-hidden="true" />
              Your Pro Subscription
            </h2>

            <p className="wallet-modal__sub">
              Balance: <strong>{balance} figs</strong>
              <span className="wallet-modal__sub-note"> · 1 fig = 1 AI analysis</span>
            </p>

            {/* Tabs */}
            <div className="wallet-tabs">
              <button
                type="button"
                className={`wallet-tab${activeTab === 'subscription' ? ' is-active' : ''}`}
                onClick={() => this.setState({ activeTab: 'subscription' })}
              >
                Pro Subscription
              </button>
              <button
                type="button"
                className={`wallet-tab${activeTab === 'buy' ? ' is-active' : ''}`}
                onClick={() => this.setState({ activeTab: 'buy' })}
              >
                Buy figs
              </button>
              <button
                type="button"
                className={`wallet-tab${activeTab === 'history' ? ' is-active' : ''}`}
                onClick={() => { this.setState({ activeTab: 'history' }); this.fetchTransactions(); }}
              >
                History
              </button>
            </div>

            {activeTab === 'history' ? (
              /* ── Transaction history ── */
              <div className="wallet-history">
                {txLoading ? (
                  <p className="wallet-history__empty">Loading…</p>
                ) : transactions.length === 0 ? (
                  <p className="wallet-history__empty">No transactions yet.</p>
                ) : (
                  <ul className="wallet-history__list">
                    {transactions.map((tx) => (
                      <li key={tx.id} className="wallet-history__row">
                        <span className={`wallet-history__delta${tx.delta > 0 ? ' is-credit' : ' is-debit'}`}>
                          {tx.delta > 0 ? `+${tx.delta}` : tx.delta}
                        </span>
                        <span className="wallet-history__type">{formatTxType(tx.type)}</span>
                        <span className="wallet-history__source">{formatTxSource(tx)}</span>
                        <span className="wallet-history__date">
                          {new Date(tx.created_at).toLocaleDateString()}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : activeTab === 'subscription' ? (
              <div className="wallet-subscription">
                <div className="wallet-subscription__hero">
                  <div className="wallet-subscription__title-row">
                    <h3>Unlock Pro</h3>
                    {proActive && <span className="wallet-pro-pill">Active</span>}
                  </div>
                  <p>
                    Pro includes monthly credits and unlocks premium writing tools:
                  </p>
                  <ul className="wallet-subscription__benefits">
                    <li>100 credits per month included</li>
                    <li>AI Agent access</li>
                    <li>AI Coach access</li>
                    <li>Proof-of-work export for your project history</li>
                    <li>Reflection history export</li>
                    <li>Social Media agent access (future feature)</li>
                  </ul>
                </div>

                {pricingLoading && <p className="wallet-history__empty">Loading pricing…</p>}
                {pricingError && <p className="wallet-error">{pricingError}</p>}
                {subscriptionError && <p className="wallet-error">{subscriptionError}</p>}

                <button
                  type="button"
                  className="wallet-buy-btn wallet-buy-btn--subscription"
                  onClick={this.handleStartSubscriptionCheckout}
                  disabled={subscriptionStarting || proActive}
                >
                  {proActive
                    ? 'Pro is active'
                    : subscriptionStarting
                      ? 'Redirecting…'
                      : `Start Pro · ${proPricing?.displayPriceYearly || proPricing?.displayPriceMonthly || '$99.00'}/year`}
                </button>

                <p className="wallet-legal-links">
                  By subscribing, you agree to yearly auto-renewal billing. Cancel anytime from account billing settings.
                </p>
              </div>
            ) : paymentSuccess ? (
              <div className="wallet-success">
                ✓ Payment confirmed — waiting for figs to be credited…
              </div>
            ) : clientSecret && stripePromise ? (
              /* ── Stripe payment form ── */
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: (() => {
                    const dark = document.documentElement.dataset.theme === 'dark';
                    return {
                      theme: dark ? 'night' : 'stripe',
                      variables: {
                        colorBackground:         dark ? '#161a20' : '#ffffff',
                        colorText:               dark ? '#f5f7fb' : '#222222',
                        colorTextSecondary:      dark ? '#9aa3b2' : '#666666',
                        colorTextPlaceholder:    dark ? '#808a99' : '#777777',
                        colorPrimary:            dark ? '#a78bfa' : '#9b5de5',
                        colorDanger:             '#ef4444',
                        borderRadius:            '8px',
                        fontFamily:              'inherit',
                      },
                      rules: {
                        '.Label': {
                          color:      dark ? '#9aa3b2' : '#555555',
                          fontSize:   '13px',
                          fontWeight: '600',
                        },
                        '.Input': {
                          backgroundColor: dark ? '#161a20' : '#ffffff',
                          color:           dark ? '#f5f7fb' : '#222222',
                          borderColor:     dark ? '#242933' : '#eeeeee',
                          boxShadow:       'none',
                        },
                        '.Input:focus': {
                          borderColor: dark ? '#a78bfa' : '#9b5de5',
                          boxShadow:   dark
                            ? '0 0 0 2px rgba(167,139,250,0.2)'
                            : '0 0 0 2px rgba(155,93,229,0.15)',
                        },
                      },
                    };
                  })(),
                }}
              >
                <div className="wallet-pkg-summary">
                  Purchasing: <strong>{selectedPkg?.coins} figs</strong> · {selectedPkg?.price}
                </div>
                <WalletCheckout
                  price={selectedPkg?.price}
                  onSuccess={this.handlePaymentSuccess}
                  onCancel={() => this.setState({ clientSecret: null })}
                />
              </Elements>
            ) : (
              /* ── Package selection ── */
              <>
                <div className="wallet-packages">
                  {packages.map((pkg) => (
                    <button
                      key={pkg.key}
                      type="button"
                      className={[
                        'wallet-pkg-card',
                        pkg.featured         ? 'is-featured' : '',
                        selected === pkg.key ? 'is-selected' : '',
                      ].filter(Boolean).join(' ')}
                      onClick={() => this.setState({ selected: pkg.key })}
                      aria-pressed={selected === pkg.key}
                    >
                      {pkg.featured && (
                        <span className="wallet-pkg-badge">Most popular</span>
                      )}
                      <div className="wallet-pkg__label">{pkg.label}</div>
                      <div className="wallet-pkg__coins">
                        <img src={FigCoinSvg} className="fig-coin-icon fig-coin-icon--sm" alt="" aria-hidden="true" />
                        {pkg.coins}
                      </div>
                      <div className="wallet-pkg__price">{pkg.price}</div>
                      <div className="wallet-pkg__runs">{pkg.runs}</div>
                    </button>
                  ))}
                </div>

                {intentError && <p className="wallet-error">{intentError}</p>}
                {pricingError && <p className="wallet-error">{pricingError}</p>}

                <button
                  className="wallet-buy-btn"
                  onClick={this.handleBuy}
                  disabled={creatingIntent || !publishableKey || pricingLoading || !selectedPkg?.coins}
                >
                  {creatingIntent
                    ? 'Preparing…'
                    : `Buy ${selectedPkg?.coins} figs · ${selectedPkg?.price}`}
                </button>

                <p className="wallet-legal-links">
                  Purchases are subject to our{' '}
                  <a href="https://storyclub.ai/terms-of-service/" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
                  and{' '}
                  <a href="https://storyclub.ai/refund-and-cancellation/" target="_blank" rel="noopener noreferrer">Refund &amp; Cancellation Policy</a>.
                </p>

                {!publishableKey && (
                  <p className="wallet-error">Payments are currently unavailable.</p>
                )}
              </>
            )}

          </div>
        </Modal>
      </>
    );
  }
}

export default CoinBalance;

